import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { GrantXpCoinsModal } from './GrantXpCoinsModal';

interface User {
  id: string;
  email: string;
  display_name: string;
  handle: string;
  role: 'player' | 'lead' | 'admin';
  // v0.7.20: per-user gender. Defaults to 'unknown' on every
  // user that hasn't been declared. The user's own /api/me
  // endpoint does NOT expose this field — it's an admin-only
  // chronicle-prompt setting, not a player-facing feature.
  gender: 'male' | 'female' | 'unknown';
  opt_outs: Record<string, boolean>;
  is_ignored: boolean;
  is_bot: boolean;
  // v0.6.2: total_xp + coin_balance added to /api/users so the
  // admin list shows the player's current state at a glance.
  // total_xp is the live sum of unvoided xp_award rows (kept
  // in sync via applyAward + the v0.6.2 backfill). coin_balance
  // is computed inline by the api as SUM(coin_transaction.amount)
  // excluding voided rows. Both are nullable in case the api
  // response shape changes — defensive render in the table.
  total_xp?: number;
  coin_balance?: number;
  created_at: string;
  updated_at: string;
}

// M5.6: full edit modal. The inline role / is_ignored editing on
// the table row stays for the common "one-click" case; the modal
// covers the rest (display name, handle, opt_outs, password reset).
// Email is intentionally NOT editable here — it's the login
// identifier and is tied to audit / SSO. If an admin needs to
// change an email, the workflow is: delete the user and recreate.
// That's annoying but rare, and it keeps the audit trail clean.
interface EditForm {
  display_name: string;
  handle: string;
  role: 'player' | 'lead' | 'admin';
  // v0.7.20: pronoun selection for the chronicle prompt.
  // 'unknown' (the default for every existing user) falls
  // back to they/them/their or rewrites without the
  // pronoun — the v0.7.14 gender-agnostic behaviour.
  gender: 'male' | 'female' | 'unknown';
  is_ignored: boolean;
  // v0.8.9: non-human actor. Editable here so the operator can mark
  // automation without SQL — see the modal note on why this differs
  // from 'ignored'.
  is_bot: boolean;
  // The opt_outs keys in use across the system. We don't show
  // all possible keys — just the ones actually set somewhere.
  // If a key isn't here, the form doesn't let you turn it on.
  // This is intentional: the form is a per-user knob for known
  // opt-outs, not a free-form toggle. New opt-outs are added at
  // the policy layer (later milestone), and the form picks them
  // up automatically.
  opt_outs: Record<string, boolean>;
  password: string;
}

const emptyEditForm = (u: User, allOptKeys: string[]): EditForm => ({
  display_name: u.display_name,
  handle: u.handle,
  role: u.role,
  // v0.7.20: defensively default to 'unknown' on any user
  // missing the field (defence in depth — the api always
  // returns it, but the form is the canonical place to
  // prevent an undefined-from-API crash).
  gender: u.gender ?? 'unknown',
  is_ignored: u.is_ignored,
  is_bot: u.is_bot,
  // Merge in any opt-out keys that exist on the user record but
  // not in the global set (defensive — shouldn't happen but keeps
  // round-trip safe).
  opt_outs: Object.fromEntries(
    allOptKeys.map((k) => [k, u.opt_outs?.[k] === true]),
  ),
  password: '',
});

export function AdminUsersPage() {
  const qc = useQueryClient();
  const users = useQuery<{ users: User[] }>({
    queryKey: ['users'],
    queryFn: () => api('/api/users'),
  });

  const create = useMutation({
    mutationFn: (body: { email: string; display_name: string; handle: string; role: string; password: string; is_ignored: boolean; is_bot: boolean; gender: 'male' | 'female' | 'unknown' }) =>
      api<{ user: User }>('/api/users', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
  const update = useMutation({
    mutationFn: ({ id, ...patch }: Partial<User> & { id: string; password?: string }) =>
      api<{ user: User }>(`/api/users/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  // Which user is waiting for the "are you sure". A single click used to run
  // DELETE straight away, and that endpoint is a HARD delete - it removes the
  // row and everything hanging off it, with no way back.
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

  const [form, setForm] = useState({ email: '', display_name: '', handle: '', role: 'player', password: '', is_ignored: false, is_bot: false, gender: 'unknown' as 'male' | 'female' | 'unknown' });
  const [err, setErr] = useState<string | null>(null);

  // M5.6: edit modal state. `editing` is the user being edited, or
  // null when the modal is closed. The form is initialised from
  // the user record each time the modal opens (not stored across
  // opens) so changes from another tab / admin are picked up.
  const [editing, setEditing] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [editErr, setEditErr] = useState<string | null>(null);

  // M5.9: admin grant modal. The target user is captured
  // when the user clicks "Grant" on a row; the modal holds
  // the form state and submits to /api/admin/users/:id/grant.
  // See ADR-0017 (.ai/decisions/0017-admin-manual-grant.md).
  const [grantTarget, setGrantTarget] = useState<User | null>(null);

  // Discover the set of opt-out keys currently in use across the
  // user base. A new opt-out is a feature flag in code, not a
  // DB column, so the form needs to know what keys are even
  // valid. (Future: lift opt-outs to a registry table when more
  // are added; for now this derived set is enough.)
  const allOptKeys = Array.from(
    new Set((users.data?.users ?? []).flatMap((u) => Object.keys(u.opt_outs ?? {}))),
  ).sort();

  useEffect(() => {
    if (editing && editForm === null) {
      setEditForm(emptyEditForm(editing, allOptKeys));
      setEditErr(null);
    }
  }, [editing, editForm, allOptKeys]);

  function closeModal() {
    setEditing(null);
    setEditForm(null);
    setEditErr(null);
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      await create.mutateAsync(form);
      setForm({ email: '', display_name: '', handle: '', role: 'player', password: '', is_ignored: false, is_bot: false, gender: 'unknown' });
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Failed to create user.');
    }
  }

  async function onSaveEdit(e: FormEvent) {
    e.preventDefault();
    if (!editing || !editForm) return;
    setEditErr(null);
    // Don't send empty fields. For password, the backend treats
    // "not present" as "don't change", so an empty string in
    // the body would be rejected by the min(8) validator.
    const patch: Record<string, unknown> = {
      display_name: editForm.display_name,
      handle: editForm.handle,
      role: editForm.role,
      // v0.7.20: include gender in the PATCH so the admin
      // edit modal can change it. The api accepts this as
      // an optional enum; existing users default to
      // 'unknown' so the first save after a user's gender
      // is set will write the value through.
      gender: editForm.gender,
      is_ignored: editForm.is_ignored,
      is_bot: editForm.is_bot,
      opt_outs: editForm.opt_outs,
    };
    if (editForm.password) patch.password = editForm.password;
    try {
      await update.mutateAsync({ id: editing.id, ...patch } as Partial<User> & { id: string; password?: string });
      closeModal();
    } catch (e2) {
      setEditErr(e2 instanceof ApiError ? e2.message : 'Failed to update user.');
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Users</h1>
          <p className="text-ink-muted">Manage player, lead, and admin accounts. Ignored users (service accounts, system actors) still receive events for audit but don't earn XP, badges, or appear on the public board.</p>
        </div>
      </header>

      <form onSubmit={onCreate} className="card grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-1">
          <label className="label" htmlFor="email">Email</label>
          <input id="email" className="input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="display_name">Display name</label>
          <input id="display_name" className="input" required value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="handle">Handle</label>
          <input id="handle" className="input" required pattern="[-A-Za-z0-9_]{2,40}" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} />
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="role">Role</label>
          <select id="role" className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="player">player</option>
            <option value="lead">lead</option>
            <option value="admin">admin</option>
          </select>
        </div>
        {/* v0.7.20: pronoun selector. Drives the per-actor
            pronoun rule in the chronicle prompt. Defaults to
            'unknown' (they/them/their). Operators usually
            set this when creating the account; can be
            changed later via the Edit modal. */}
        <div className="space-y-1">
          <label className="label" htmlFor="create-gender">Pronouns (chronicle)</label>
          <select id="create-gender" className="input" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value as 'male' | 'female' | 'unknown' })}>
            <option value="unknown">unknown — they/them/their</option>
            <option value="male">male — he/him/his</option>
            <option value="female">female — she/her/her</option>
          </select>
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="password">Password</label>
          <input id="password" className="input" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        <div className="sm:col-span-2 lg:col-span-5 flex items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-ink-muted">
            <input
              type="checkbox"
              checked={form.is_ignored}
              onChange={(e) => setForm({ ...form, is_ignored: e.target.checked })}
              className="h-4 w-4"
            />
            Mark as ignored (non-player / service account)
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-ink-muted">
            <input
              type="checkbox"
              checked={form.is_bot}
              onChange={(e) => setForm({ ...form, is_bot: e.target.checked })}
              className="h-4 w-4"
            />
            🤖 Automation (keeps earning)
          </label>
          <button className="btn-primary" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create user'}
          </button>
          {err ? <p className="text-sm text-accent-danger" role="alert">{err}</p> : null}
        </div>
      </form>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-ink-muted">
            <tr>
              <th className="py-2">Handle</th>
              <th>Display name</th>
              <th>Email</th>
              <th>Role</th>
              {/* v0.7.20: pronoun column. Shows the current
                  per-user setting so admins can scan the list
                  for users still on the default 'unknown'
                  value (the typical "haven't been set yet"
                  state for long-time users). */}
              <th>Pronouns</th>
              <th>Status</th>
              {/* v0.6.2: XP + coins columns. Right-aligned numerics
                  match the leaderboard convention. Coins use the
                  golden accent (text-amber-400) to mirror the
                  CoinBalanceChip. Total XP is the raw live number
                  from the api; a small L0..L5 badge underneath
                  uses the same floor((xp/100)^(2/3)) formula as
                  the skill tree so the operator can see tier at
                  a glance without opening the player. */}
              <th className="text-right">XP</th>
              <th className="text-right">Coins</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(users.data?.users ?? []).map((u) => (
              <tr key={u.id} className="border-t border-white/5">
                <td className="py-2 font-mono">{u.handle}</td>
                <td>{u.display_name}</td>
                <td className="text-ink-muted">{u.email}</td>
                <td>
                  <select
                    className="input py-1"
                    value={u.role}
                    onChange={(e) => update.mutate({ id: u.id, role: e.target.value as User['role'] })}
                  >
                    <option value="player">player</option>
                    <option value="lead">lead</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                {/* v0.7.20: pronoun cell. Inline select mirrors
                    the role-select pattern — admins can flip the
                    pronoun in-place without opening the modal.
                    The select writes to the same PATCH endpoint
                    with `body: { gender }`. */}
                <td>
                  <select
                    className="input py-1 text-xs"
                    value={u.gender}
                    onChange={(e) => update.mutate({ id: u.id, gender: e.target.value as User['gender'] })}
                    title="Pronoun selection for the chronicle prompt. unknown = they/them/their."
                  >
                    <option value="unknown">they/them/their</option>
                    <option value="male">he/him/his</option>
                    <option value="female">she/her/her</option>
                  </select>
                </td>
                <td>
                  <label className="inline-flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={u.is_ignored}
                      onChange={(e) => update.mutate({ id: u.id, is_ignored: e.target.checked })}
                      className="h-3.5 w-3.5"
                      title="Ignored: events recorded for audit but no XP/badges/public board presence"
                    />
                    {u.is_ignored ? <span className="rounded bg-ink-dim/30 px-1.5 py-0.5 text-ink-dim">ignored</span> : null}
                    {u.is_bot ? <span className="rounded bg-violet-500/20 px-1.5 py-0.5 text-violet-300" title="non-human actor — earns normally">🤖 bot</span> : null}
                    {!u.is_ignored && !u.is_bot ? <span className="text-ink-muted">active</span> : null}
                  </label>
                </td>
                <td className="text-ink-muted">{new Date(u.created_at).toLocaleDateString()}</td>
                {/* v0.6.2: XP + coins cells. is_ignored users show
                    a dash for XP (they don't accumulate); coins
                    still show (admin grants and quest rewards can
                    land regardless of is_ignored, since the user
                    object is real). The L0..L5 sub-line uses the
                    same formula as the skill tree. */}
                <td className="text-right font-mono tabular-nums whitespace-nowrap">
                  {u.is_ignored ? <span className="text-ink-muted">—</span> : (
                    <>
                      {u.total_xp ?? 0}
                      <div className="text-[10px] text-ink-muted">
                        L{Math.max(0, Math.floor(Math.pow((u.total_xp ?? 0) / 100, 2 / 3)))}
                      </div>
                    </>
                  )}
                </td>
                <td className="text-right font-mono tabular-nums text-amber-400">
                  {u.coin_balance ?? 0}
                </td>
                <td className="text-right whitespace-nowrap">
                  <button
                    className="btn-ghost"
                    onClick={() => setGrantTarget(u)}
                    type="button"
                  >
                    Grant
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={() => setEditing(u)}
                    type="button"
                  >
                    Edit
                  </button>
                  {confirmingDeleteId === u.id ? (
                    <>
                      {/* Names the account, because "are you sure?" with no name is
                          a question you can answer without reading the row. */}
                      <span className="ml-2 text-xs text-red-300">
                        Permanently delete {u.display_name || '@' + u.handle}?
                      </span>
                      <button
                        className="btn-danger ml-2"
                        type="button"
                        onClick={() => {
                          remove.mutate(u.id);
                          setConfirmingDeleteId(null);
                        }}
                      >
                        Yes, delete
                      </button>
                      <button
                        className="btn ml-2"
                        type="button"
                        onClick={() => setConfirmingDeleteId(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn-danger ml-2"
                      type="button"
                      onClick={() => setConfirmingDeleteId(u.id)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!(users.data?.users ?? []).length ? (
              <tr><td colSpan={10} className="py-4 text-center text-ink-muted">No users yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {grantTarget ? <GrantXpCoinsModal target={grantTarget} onClose={() => setGrantTarget(null)} /> : null}

      {editing && editForm && (
        // M5.6: edit modal. Inline, no Radix dependency. The
        // backdrop is a fixed-position click target that closes
        // the modal (cancel button is the only way to close
        // without saving, but clicking the backdrop matches
        // user expectations for non-confirmation modals).
        // The dialog itself uses stopPropagation so clicks
        // inside don't bubble up to the backdrop.
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-user-title"
        >
          <div
            className="w-full max-w-lg rounded-lg border border-white/5 bg-bg-panel p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="edit-user-title" className="text-lg font-semibold">
              Edit user — {editing.handle}
            </h2>
            <p className="mt-1 text-xs text-ink-muted">
              Email is not editable. Last updated {new Date(editing.updated_at).toLocaleString()}.
            </p>
            {/* v0.6.2: live XP + coins shown above the form so the
                admin has context while editing. Mirrors the table
                columns. Same L0..L5 subtext. */}
            <div className="mt-2 flex gap-4 text-sm">
              <div>
                <span className="text-ink-muted">XP: </span>
                <span className="font-mono tabular-nums">{editing.total_xp ?? 0}</span>
                <span className="ml-1 text-[10px] text-ink-muted">
                  L{Math.max(0, Math.floor(Math.pow((editing.total_xp ?? 0) / 100, 2 / 3)))}
                </span>
              </div>
              <div>
                <span className="text-ink-muted">Coins: </span>
                <span className="font-mono tabular-nums text-amber-400">{editing.coin_balance ?? 0}</span>
              </div>
            </div>
            <form onSubmit={onSaveEdit} className="mt-4 space-y-3">
              <div className="space-y-1">
                <label className="label" htmlFor="edit-display-name">Display name</label>
                <input
                  id="edit-display-name"
                  className="input"
                  required
                  maxLength={120}
                  value={editForm.display_name}
                  onChange={(e) => setEditForm((f) => f ? { ...f, display_name: e.target.value } : f)}
                />
              </div>
              <div className="space-y-1">
                <label className="label" htmlFor="edit-handle">Handle</label>
                <input
                  id="edit-handle"
                  className="input font-mono"
                  required
                  pattern="[A-Za-z0-9_-]{2,40}"
                  value={editForm.handle}
                  onChange={(e) => setEditForm((f) => f ? { ...f, handle: e.target.value } : f)}
                />
                <p className="text-xs text-ink-dim">Lowercase on save. Used on the public board and the skill tree.</p>
              </div>
              <div className="space-y-1">
                <label className="label" htmlFor="edit-role">Role</label>
                <select
                  id="edit-role"
                  className="input"
                  value={editForm.role}
                  onChange={(e) => setEditForm((f) => f ? { ...f, role: e.target.value as EditForm['role'] } : f)}
                >
                  <option value="player">player</option>
                  <option value="lead">lead</option>
                  <option value="admin">admin</option>
                </select>
              </div>
              {/* v0.7.20: pronoun selector in the edit modal.
                  Drives the per-actor pronoun rule in the
                  chronicle prompt. Defaults to 'unknown' —
                  the they/them/their fallback that preserves
                  the v0.7.14 gender-agnostic behaviour for
                  users who haven't been declared. */}
              <div className="space-y-1">
                <label className="label" htmlFor="edit-gender">Pronouns (chronicle)</label>
                <select
                  id="edit-gender"
                  className="input"
                  value={editForm.gender}
                  onChange={(e) => setEditForm((f) => f ? { ...f, gender: e.target.value as EditForm['gender'] } : f)}
                >
                  <option value="unknown">unknown — they/them/their</option>
                  <option value="male">male — he/him/his</option>
                  <option value="female">female — she/her/her</option>
                </select>
                <p className="text-xs text-ink-dim">Used by the chronicle prompt per actor. "Unknown" falls back to they/them/their (or rewrite without the pronoun), matching the v0.7.14 behaviour.</p>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-ink-muted">
                <input
                  type="checkbox"
                  checked={editForm.is_ignored}
                  onChange={(e) => setEditForm((f) => f ? { ...f, is_ignored: e.target.checked } : f)}
                />
                Ignored (non-player / service account — earns nothing)
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-ink-muted">
                <input
                  type="checkbox"
                  checked={editForm.is_bot}
                  onChange={(e) => setEditForm((f) => f ? { ...f, is_bot: e.target.checked } : f)}
                />
                🤖 Automation (non-human, but still earns)
              </label>
              <p className="text-xs text-ink-dim">
                Ignored and automation are different: <em>ignored</em> gets no XP, coins,
                badges or board presence. <em>Automation</em> earns normally and is only
                marked, so you can see how much of the team's progress comes from
                non-human input.
              </p>

              {allOptKeys.length > 0 && (
                <fieldset className="rounded-md border border-white/5 p-3">
                  <legend className="px-1 text-xs uppercase tracking-wide text-ink-muted">Opt-outs</legend>
                  <div className="space-y-1.5">
                    {allOptKeys.map((k) => (
                      <label key={k} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={editForm.opt_outs[k] === true}
                          onChange={(e) => setEditForm((f) => f ? {
                            ...f,
                            opt_outs: { ...f.opt_outs, [k]: e.target.checked },
                          } : f)}
                        />
                        <code className="text-xs">{k}</code>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}

              <div className="space-y-1">
                <label className="label" htmlFor="edit-password">Set new password</label>
                <input
                  id="edit-password"
                  className="input"
                  type="password"
                  minLength={8}
                  placeholder="Leave blank to keep current"
                  value={editForm.password}
                  onChange={(e) => setEditForm((f) => f ? { ...f, password: e.target.value } : f)}
                />
                <p className="text-xs text-ink-dim">8+ characters. Hashed before storage. Audit-logged as <code>password_changed: true</code> (never the new value).</p>
              </div>

              {editErr && <p className="text-sm text-accent-danger" role="alert">{editErr}</p>}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn-ghost" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={update.isPending}>
                  {update.isPending ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
